<script setup lang="ts">
import Slider from '@/widgets/slider/index';
import ProductCard from '@/widgets/productCard/index';
import ArrowRight from '@/shared/ui/Icons/ArrowRight.vue';

import type { IProductSlider } from '@/widgets/productsSlider/model/types/productSlider';
import { useProducts } from '@/entities/products/composables/useProducts';

const props = defineProps<IProductSlider>();
const { t } = useI18n();
const { products } = await useProducts(props.queryParams, props.productsKey);
</script>

<template>
  <div class="products-slider">
    <div class="products-slider__header">
      <h3 class="products-slider__title">{{ title }}</h3>
      <nuxt-link
        v-if="link"
        class="products-slider__link"
        :to="link"
        :aria-label="t('moreProducts')"
      >
        {{ t('moreProducts') }}
        <ArrowRight class="products-slider__icon" />
      </nuxt-link>
    </div>
    <Slider
      :slides="products"
      :items-to-show="4"
      effect="slide"
      align="start"
      is-show-navigation
      :gap="20"
      loop
      class="products-slider__slider"
    >
      <template #slide="{ slide }">
        <div class="products-slider__slide">
          <ProductCard :product="slide" />
        </div>
      </template>
    </Slider>
  </div>
</template>

<style lang="less">
.products-slider {
  .container-d();
  .flex-column(@size-x5);

  position: relative;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__link {
    .color-change-hover();

    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    line-height: 100%;
    font-weight: 600;
    color: @color-oxford-blue;

    &:hover .products-slider__icon path {
      transition: fill @A linear;
      fill: @color-tango;
    }
  }

  &__icon {
    width: @size-x4;
    height: @size-x4;
  }

  &__title {
    .h2();
  }

  &__slider {
    width: 100%;
    height: 100%;

    .embla__viewport {
      padding: @size-x2 0;
    }

    .embla__button._prev {
      left: -44px;
    }

    .embla__button._next {
      right: -44px;
    }
  }

  @media @1600 {
    &__slider {
      .embla__button {
        &._prev {
          left: -@size-x4;
        }

        &._next {
          right: -@size-x4;
        }
      }
    }
  }

  @media @1500 {
    &__slider {
      padding: 0 @size-x5;

      .embla__button {
        &._prev {
          left: -@size-x2;
        }

        &._next {
          right: -@size-x2;
        }
      }
    }
  }

  @media @desktop {
    &__slider {
      .embla__button {
        &._prev {
          left: @size-x5;
        }

        &._next {
          right: @size-x5;
        }
      }
    }
  }
}
</style>
